import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { EmailVerificationViewStore } from "../../store";
import { EmailVerificationView } from "../view/EmailVerificationView";
import { EmailVerificationUpdateStore } from "../../store";
import { EmailVerificationUpdate } from "../update/EmailVerificationUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type EmailVerificationModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const EmailVerificationModalContent: React.FC<EmailVerificationModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new EmailVerificationUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new EmailVerificationViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <EmailVerificationView
            emailVerificationViewStore={
              storeInfo.store as EmailVerificationViewStore
            }
          />
        )}
        {storeInfo.type === "update" && (
          <EmailVerificationUpdate
            emailVerificationUpdateStore={
              storeInfo.store as EmailVerificationUpdateStore
            }
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
